import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
// eslint-disable-next-line import/no-extraneous-dependencies
import { uniqBy } from 'lodash';
import type { AddressType } from '@sweetgreen/garnish';
import {
  BodyText,
  Button,
  IllusEmployeeBag,
  Image,
  LoadingPlaceholder,
  theme,
  VStack,
} from '@sweetgreen/garnish';

import {
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenTitle,
  OrderCard,
} from '@order/components';
import type { OrdersResponseSuccess } from '@order/graphql';
import { useLineItemsNames } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import { useOrderStatusNavigation } from '../OrderStatusScreen/OrderStatus.navigation';
import { useOrderStatusTelemetry } from '../OrderStatusScreen/OrderStatus.telemetry';
import { useOrderHistoryQuery } from './GraphQL/Orders.generated';

export const OrdersScreen = () => {
  const { t } = useLocalizationContext();
  const data = usePaginatedOrders();

  return (
    <AccountScreenContainer screen="orders">
      <AccountScreenTitle title={t('account.orders.title')} />
      <AccountScreenSection>
        <OrdersScreenContent {...data} />
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

export const OrdersScreenContent = ({
  orders,
  canLoadMore,
  isLoading,
  handleLoadMore,
}: ReturnType<typeof usePaginatedOrders>) => {
  const { t } = useLocalizationContext();

  if (isLoading && orders.length === 0) return <LoadingState />;

  if (!isLoading && orders.length === 0) return <EmptyState />;

  return (
    <>
      <VStack>
        {orders.map((order) => (
          <ConnectedOrderCard key={order.id} order={order} />
        ))}
      </VStack>
      {orders.length > 0 && canLoadMore ? (
        <Button
          testID="account.orders.load-more"
          palette="secondary"
          accessibilityRole="button"
          accessibilityLabel={t('account.orders.load-more')}
          accessibilityHint={t('account.orders.load-more')}
          style={styles.button}
          isLoading={isLoading}
          onPress={handleLoadMore}
        >
          {t('account.orders.load-more')}
        </Button>
      ) : null}
    </>
  );
};

type Orders = OrdersResponseSuccess['orders'];
const usePaginatedOrders = () => {
  const [canLoadMore, setCanLoadMore] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [orders, setOrders] = React.useState<Orders>([]);
  const variables = { input: { take: 10, page } };
  const [response, fetchOrderHistory] = useOrderHistoryQuery({
    variables,
    pause: true,
    requestPolicy: 'cache-and-network',
  });
  const isLoading = response.fetching;
  const successResponse = response.data?.orders as OrdersResponseSuccess;
  const ordersResponse = React.useMemo(
    () => successResponse?.orders ?? [],
    [successResponse?.orders],
  );

  useFocusEffect(
    useCallback(() => {
      fetchOrderHistory();
    }, [fetchOrderHistory]),
  );

  React.useEffect(() => {
    const empty = orders.length === 0 && ordersResponse.length === 0;

    setCanLoadMore(empty || ordersResponse.length === 10);
    setOrders(uniqBy([...orders, ...ordersResponse], 'id'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersResponse]);
  const handleLoadMore = React.useCallback(() => {
    setPage(Number(page) + 1);
    fetchOrderHistory();
  }, [page, fetchOrderHistory, setPage]);

  return { orders, canLoadMore, isLoading, handleLoadMore };
};

const ConnectedOrderCard = ({ order }: { readonly order: Orders[0] }) => {
  const orderLineItemsNames = useLineItemsNames(order?.lineItems);

  const { handleOrderStatus } = useOrderStatusNavigation();
  const { handleOrderStatusEvent } = useOrderStatusTelemetry();
  const handlePress = React.useCallback(() => {
    handleOrderStatus(order.id);
    handleOrderStatusEvent('account.orders');
  }, [order.id, handleOrderStatus, handleOrderStatusEvent]);

  return (
    <OrderCard
      orderId={order.id}
      orderType={convertOrderType(order)}
      wantedTime={order.wantedTime}
      restaurantName={order.restaurant.name}
      addressName={order.deliveryOrderDetail?.address?.name ?? undefined}
      description={orderLineItemsNames}
      total={order.total ?? 0}
      assetUrl={order.asset?.url ?? ''}
      onPress={handlePress}
    />
  );
};

const convertOrderType = (order: Orders[0]): AddressType => {
  if (order.restaurant.isOutpost) return 'outpost';

  return order.orderType.toLowerCase() as AddressType;
};

const LoadingState = () => {
  return <LoadingPlaceholder palette="quinoa" rows={4} rowHeight={156} />;
};

const EmptyState = () => {
  const { t } = useLocalizationContext();

  return (
    <View style={styles.emptyContainer}>
      <Image
        source={IllusEmployeeBag}
        style={styles.emptyImage}
        accessibilityIgnoresInvertColors
      />
      <BodyText style={styles.noResultsText}>
        {t('account.orders.empty')}
      </BodyText>
    </View>
  );
};

const styles = StyleSheet.create({
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyImage: {
    width: 220,
    height: 220,
    marginBottom: theme.spacing.medium,
  },
  noResultsText: {
    textAlign: 'center',
  },
  button: {
    minWidth: 200,
    maxWidth: 200,
    marginHorizontal: 'auto',
    marginTop: theme.spacing.large,
  },
});
